import { useEffect, useState } from "react";
import { apiRequest } from "../../services/api";
const field = "mt-1 min-h-10 w-full rounded-lg border bg-white p-2 text-sm";
const button =
  "rounded-lg border px-3 py-2 text-sm font-semibold disabled:opacity-40";
export default function SettlementCorrections({
  data,
  companyId,
  accessToken,
  canManage,
  canWrite,
  busy,
  run,
  requestKey,
  onSaved,
  onBegin,
}) {
  const [target, setTarget] = useState(null),
    [reason, setReason] = useState(""),
    [confirmed, setConfirmed] = useState(false),
    [replacement, setReplacement] = useState({});
  useEffect(() => {
    setTarget(null);
    setConfirmed(false);
  }, [data]);
  function open(kind, row) {
    onBegin?.();
    setTarget({ kind, row });
    setReason("");
    setConfirmed(false);
    if (kind === "supersede")
      setReplacement(
        Object.fromEntries(
          [
            "channelId",
            "externalTransactionId",
            "settlementReference",
            "bankCreditReference",
            "transactionDate",
            "transactionDateThrough",
            "evidenceScope",
            "bankDate",
            "gross",
            "fees",
            "feeTax",
            "net",
            "providerEvidence",
            "bankEvidence",
            "note",
          ].map((k) => [
            k,
            ["transactionDate", "transactionDateThrough", "bankDate"].includes(
              k,
            )
              ? (row[k] || row.transactionDate).slice(0, 10)
              : k === "evidenceScope"
                ? row[k] || "SINGLE_TRANSACTION"
                : row[k],
          ]),
        ),
      );
  }
  function submit(e) {
    e.preventDefault();
    if (!target || !confirmed || reason.trim().length < 20 || busy) return;
    const path =
      target.kind === "retire"
        ? `/channels/${target.row.id}/retire`
        : target.kind === "release"
          ? `/matches/${target.row.id}/release`
          : `/${target.row.id}/supersede`;
    const body =
      target.kind === "supersede"
        ? {
            ...replacement,
            ...(replacement.evidenceScope === "BATCH_CREDIT"
              ? { batchCreditConfirmed: confirmed }
              : { singleTransactionCreditConfirmed: confirmed }),
            reason,
            correctionConfirmed: confirmed,
          }
        : { reason, correctionConfirmed: confirmed };
    run(
      async () => {
        await apiRequest(
          `/accounting/${companyId}/settlement-evidence${path}`,
          {
            accessToken,
            method: "POST",
            body: {
              ...body,
              requestKey: requestKey("correction:" + path, body),
            },
          },
        );
        return apiRequest(`/accounting/${companyId}/settlement-evidence`, {
          accessToken,
        });
      },
      (r) => {
        onSaved(r);
        setTarget(null);
        setConfirmed(false);
      },
    );
  }
  return (
    <section className="space-y-4 rounded-xl border border-amber-200 bg-amber-50/30 p-4">
      <h3 className="font-semibold">
        Reviewed corrections — history is retained
      </h3>
      <p className="text-sm text-slate-600">
        These actions change evidence-review eligibility only. They do not
        reverse a voucher, change a bank balance or issue a refund. Retired
        channels cannot accept new capture or matching. Existing matches must be
        released before their evidence is superseded.
      </p>
      <details>
        <summary className="cursor-pointer font-medium">
          Channel lifecycle
        </summary>
        <ul className="mt-3 space-y-2">
          {data.channels.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-3">
              <span>
                {c.name} · {c.kind} · {c.method}
              </span>
              {c.retiredId ? (
                <span className="text-sm">
                  Retired · {c.retirement?.reason || "History retained"}
                </span>
              ) : (
                <>
                  <span className="text-sm">Active</span>
                  {canManage && (
                    <button
                      className={button}
                      disabled={busy}
                      onClick={() => open("retire", c)}
                    >
                      Retire {c.name}
                    </button>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      </details>
      <div className="space-y-3">
        {data.evidence.map((e) => (
          <div key={e.id} className="rounded-lg border bg-white p-3">
            <p className="font-medium">
              {e.externalTransactionId} · {e.bankCreditReference}
            </p>
            {e.posting && (
              <p className="text-sm">
                {e.posting.reversal
                  ? "Accounting reversal recorded · release allocations and supersede evidence explicitly."
                  : "Posted settlement · allocations frozen; coordinated financial correction required."}
              </p>
            )}
            {e.supersededId ? (
              <p className="text-sm">
                Superseded · Replacement{" "}
                {e.supersession?.replacementId || "retained in history"} ·{" "}
                {e.supersession?.reason}
              </p>
            ) : e.match ? (
              <div className="mt-2 flex items-center gap-3">
                <span className="text-sm">
                  Active manual match · {e.match.snapshot.receiptNumber}
                </span>
                {canWrite && (
                  <button
                    className={button}
                    disabled={busy || (!!e.posting && !e.posting.reversal)}
                    onClick={() => open("release", e.match)}
                  >
                    Release incorrect match for {e.externalTransactionId}
                  </button>
                )}
              </div>
            ) : (
              canWrite && (
                <button
                  className={button}
                  disabled={busy || (!!e.posting && !e.posting.reversal)}
                  onClick={() => open("supersede", e)}
                >
                  Supersede evidence {e.externalTransactionId}
                </button>
              )
            )}
            {(e.matchHistory || [])
              .filter((m) => m.releasedId)
              .map((m) => (
                <p className="mt-2 text-sm text-slate-600" key={m.id}>
                  Released match · {m.snapshot.receiptNumber} ·{" "}
                  {m.release?.reason || "History retained"} ·{" "}
                  {m.release?.createdAt || ""}
                </p>
              ))}
            {(e.activeMatches || []).slice(1).map((m) => (
              <div className="mt-2 flex items-center gap-3" key={m.id}>
                <span className="text-sm">
                  Active allocation · {m.snapshot.receiptNumber} · gross ₹
                  {m.amount}
                </span>
                {canWrite && (
                  <button
                    className={button}
                    disabled={busy || (!!e.posting && !e.posting.reversal)}
                    onClick={() => open("release", m)}
                  >
                    Release allocation {m.id.slice(0, 8)} for{" "}
                    {e.externalTransactionId}
                  </button>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
      {target && (
        <form
          onSubmit={submit}
          className="space-y-3 rounded-xl border bg-white p-4"
        >
          <fieldset disabled={busy} className="space-y-3">
            <legend className="font-semibold">
              {target.kind === "retire"
                ? "Retire collection channel"
                : target.kind === "release"
                  ? "Release this exact match"
                  : "Supersede evidence with a corrected record"}
            </legend>
            <p className="text-sm">
              Target:{" "}
              {target.row.name ||
                target.row.externalTransactionId ||
                target.row.snapshot.receiptNumber}
              . Original history remains unchanged. This action cannot be undone
              by deleting records.
            </p>
            {target.kind === "supersede" && (
              <>
                <p className="text-sm">
                  Original gross ₹{target.row.gross}, fees ₹{target.row.fees},
                  fee tax ₹{target.row.feeTax}, net ₹{target.row.net}. Review
                  every replacement value below.
                </p>
                <div className="grid gap-3 md:grid-cols-3">
                  <label className="text-sm">
                    Replacement credit scope
                    <select
                      className={field}
                      value={replacement.evidenceScope}
                      onChange={(e) => {
                        setReplacement({
                          ...replacement,
                          evidenceScope: e.target.value,
                        });
                        setConfirmed(false);
                      }}
                    >
                      <option value="SINGLE_TRANSACTION">
                        Single transaction / partial credit
                      </option>
                      <option value="BATCH_CREDIT">Batch bank credit</option>
                    </select>
                  </label>
                  <label className="text-sm">
                    Replacement collection channel
                    <select
                      className={field}
                      required
                      value={replacement.channelId}
                      onChange={(e) => {
                        setReplacement({
                          ...replacement,
                          channelId: e.target.value,
                        });
                        setConfirmed(false);
                      }}
                    >
                      <option value="">Choose active channel</option>
                      {data.channels
                        .filter((c) => !c.retiredId)
                        .map((c) => (
                          <option value={c.id} key={c.id}>
                            {c.name} · {c.kind} · {c.method}
                          </option>
                        ))}
                    </select>
                  </label>
                  {Object.entries({
                    externalTransactionId: "External transaction ID",
                    settlementReference: "Settlement reference",
                    bankCreditReference: "Bank credit reference",
                    transactionDate: "Transaction date",
                    transactionDateThrough: "Transactions through date",
                    bankDate: "Bank date",
                    gross: "Gross ₹",
                    fees: "Fees ₹",
                    feeTax: "Fee tax ₹",
                    net: "Net ₹",
                    providerEvidence: "Provider source reference",
                    bankEvidence: "Bank source reference",
                    note: "Replacement evidence note",
                  }).map(([k, label]) => (
                    <label className="text-sm" key={k}>
                      Replacement {label}
                      <input
                        className={field}
                        required
                        type={
                          [
                            "transactionDate",
                            "transactionDateThrough",
                            "bankDate",
                          ].includes(k)
                            ? "date"
                            : "text"
                        }
                        value={replacement[k]}
                        minLength={k === "note" ? 20 : undefined}
                        maxLength={
                          k === "note"
                            ? 1000
                            : ["providerEvidence", "bankEvidence"].includes(k)
                              ? 500
                              : 150
                        }
                        onChange={(e) => {
                          setReplacement({
                            ...replacement,
                            [k]: e.target.value,
                          });
                          setConfirmed(false);
                        }}
                      />
                    </label>
                  ))}
                </div>
              </>
            )}
            <label className="block text-sm">
              Correction reason
              <textarea
                className={field}
                required
                minLength={20}
                maxLength={1000}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setConfirmed(false);
                }}
              />
            </label>
            <label className="block text-sm">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />{" "}
              I reviewed this exact correction and its retained history
              {target.kind === "supersede"
                ? ", including both source documents, the single/batch credit scope and its transaction date range"
                : ""}
              .
            </label>
            <div className="flex gap-3">
              <button
                className={button}
                disabled={!confirmed || reason.trim().length < 20}
              >
                Confirm correction — no financial posting
              </button>
              <button
                type="button"
                className={button}
                onClick={() => setTarget(null)}
              >
                Cancel correction
              </button>
            </div>
          </fieldset>
        </form>
      )}
    </section>
  );
}
